import { useEffect, useState } from 'react'
import { ExecPromiseOptions } from '@cjdev-internal/visual-stack-x/hooks/useAPIManager'
import { ToastOptions } from '@cjdev-internal/visual-stack-x/components/Toast'
import {
  AlternatePayeeApiConfig,
  AlternatePayeeList, AlternatePayeeServices,
  useFetchProps
} from '../../../../types'


const INITIAL_VALUE : AlternatePayeeList = [
  {
    id: 0,
    name: '',
    paymentMethodId: 0,
    paymentCurrency: '',
    accountType: '',
    routingNumber: '',
    accountNumber: '',
    payableTo: '',
    finInstitutionName: '',
    altairVendorId: '',
    lastModified: ''
  }
]

const useAlternatePayee = (
  {
    restApi,
    user,
    showForbiddenErrorPage,
    apiConfig,
    services
  }: useFetchProps) => {

  const [alternatePayeeData, setAlternatePayeeData] = useState<AlternatePayeeList>(INITIAL_VALUE)
  const [isLoading, setIsLoading] = useState(true)

  const showNotification = (message: string, statusCode: number) => {
    const type = statusCode > 199 && statusCode < 300 ? 'success': 'warning';
    const notification: ToastOptions = {
      type,
      message,
      duration: 5000
    }
    restApi.showNotification(notification)
  }


  // Response handlers

  const onGetSuccess = async (res: Response) => {
    const { data, status, message } = await services.responseParser.get(res);

    if (!data) {
      showNotification(message!, status);
      setAlternatePayeeData(INITIAL_VALUE);
      return;
    }
    setAlternatePayeeData(data);
  };

  const onError = async (res: Response) => {
    const { message, status } = await services.responseParser.error(res);
    showForbiddenErrorPage(status === 403)
    showNotification(message!, status);
  };

  const onSuccess = async (res: Response) => {
    await get();
    const { message, status } = await services.responseParser.persist(res, {
      created: 'Payee successfully created.',
      updated: 'Payee successfully updated.'
    });
    showNotification(message!, status);
  };

  const before = () => setIsLoading(true)
  const after = () => setIsLoading(false)

  // fetch

  const update = async (data: string) => {
    const promise = () => promisePut(apiConfig, data, services.api)
    const options: ExecPromiseOptions = { before, after, onSuccess, onError }
    await restApi.fetch(promise, options)
  }

  const save = async (data: string) => {
    const promise = () => promisePost(apiConfig, data, services.api)
    const options: ExecPromiseOptions = { before, after, onSuccess, onError }
    await restApi.fetch(promise, options)
  }

  const get = async () => {
    const promise = () => promiseGet(apiConfig, services.api)
    const options: ExecPromiseOptions = { before, after, onSuccess: onGetSuccess, onError }
    await restApi.fetch(promise, options)
  }

  useEffect(() => {
    get();
  },[user])


  return [ alternatePayeeData, { save, update }, isLoading ] as const;
}


const promiseGet = (config: AlternatePayeeApiConfig, api: AlternatePayeeServices['api']) => {
  return api.get(config);
}

const promisePost = (config: AlternatePayeeApiConfig, data: string, api: AlternatePayeeServices['api']) => {
  return api.post(config, data);
}

const promisePut = (config: AlternatePayeeApiConfig, data: string, api: AlternatePayeeServices['api']) => {
  return api.put(config, data);
}

export default useAlternatePayee;